import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import {
    MDBDropdown, MDBDropdownToggle, MDBDropdownMenu,
    MDBListGroup, MDBListGroupItem, MDBCheckbox, MDBInput, MDBInputGroup
} from 'mdb-react-ui-kit';
import { getInfluencersList, addContentForm } from '../services/api';

const ContentForm = ({ isOpen, handleClose, selectedInfluencers, campaignID }) => {
    const [contentCreatorList, setContentCreatorList] = useState([]);
    const [selectedContentCreator, setSelectedContentCreator] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [description, setDescription] = useState('');
    const [contentUrl, setContentUrl] = useState('');
    const [workplatform, setWorkplatform] = useState('');
    const [filteredCreators, setFilteredCreators] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data } = await getInfluencersList();
                const filteredData = data.filter(influencer => selectedInfluencers.includes(influencer.influencerId));
                setContentCreatorList(filteredData);
                setFilteredCreators(filteredData); // Initial filter
            } catch (error) {
                console.error('Error fetching influencers data:', error);
            }
        };

        fetchData();
    }, [selectedInfluencers]);

    useEffect(() => {
        setFilteredCreators(contentCreatorList);
    }, [contentCreatorList]);

    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase().trim();
        setSearchTerm(value);
        setFilteredCreators(value
            ? contentCreatorList.filter(creator => creator.creatorName.toLowerCase().includes(value))
            : contentCreatorList);
    };

    const handleSelectContentCreator = (creatorID) => {
        setSelectedContentCreator(creatorID === selectedContentCreator ? null : creatorID);
    };

    const saveContentToDatabase = async (contentData) => {
        try {
            const response = await addContentForm(contentData);
            if (response && response.status === 200) {
                console.log("Campaign content saved successfully:", response.data);
                return true; // Indicate success
            } else {
                console.error("Failed to save campaign content:", response?.data?.message || "Unknown error");
                return false; // Indicate failure
            }
        } catch (error) {
            console.error("Error creating campaign:", error.response?.data || error.message);
            return false;
        }
    };
    
    const validateAndPrepareContent = () => {
        if (!selectedContentCreator || !description.trim() || !contentUrl.trim() || !workplatform) {
            alert("All fields are required.");
            return null; // Return `null` if validation fails
        }
    
        return {
            CampaignID: campaignID,
            Description: description.trim(),
            ContentURL: contentUrl.trim(),
            Creator: String(selectedContentCreator),
            WorkPlatformId: workplatform
        };
    };
    
    const handleSave = async () => {
        const contentData = validateAndPrepareContent();
        if (!contentData) return; // Stop execution if validation fails
    
        const isSaved = await saveContentToDatabase(contentData);
        if (isSaved) {
            handleClose(); // Close modal only if successfully saved
        }
    };
    
    const handleAddMore = async () => {
        const contentData = validateAndPrepareContent();
        if (!contentData) return; // Stop execution if validation fails
    
        const isSaved = await saveContentToDatabase(contentData);
        if (isSaved) {
            setSelectedContentCreator(null);
            setDescription('');
            setContentUrl('');
            setWorkplatform('');
        }
    };
    

    return (
        <Modal show={isOpen} onHide={handleClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Content Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    {/* Content Creator Dropdown */}
                    <Form.Group className="mb-3">
                        <Form.Label>Content Creator *</Form.Label>
                        <MDBDropdown className="w-100">
                            <MDBDropdownToggle type="button">
                                {selectedContentCreator ?
                                    contentCreatorList.find(creator => creator.influencerId === selectedContentCreator)?.creatorName
                                    : "Select Content Creator"}
                            </MDBDropdownToggle>
                            <MDBDropdownMenu className="w-100 p-2" style={{ maxHeight: "300px", overflowY: "auto" }} autoClose="outside">
                                <MDBInputGroup className="mb-2">
                                    <MDBInput label="Search creator" value={searchTerm} onChange={handleSearch} />
                                </MDBInputGroup>
                                <MDBListGroup>
                                    {filteredCreators.map((creator) => (
                                        <MDBListGroupItem
                                            key={creator.influencerId}
                                            className="d-flex align-items-center"
                                            active={selectedContentCreator === creator.influencerId}
                                            style={{ cursor: "pointer", borderBottom: "1px solid #ddd" }}
                                            onClick={() => handleSelectContentCreator(creator.influencerId)}
                                        >
                                            <MDBCheckbox
                                                checked={selectedContentCreator === creator.influencerId}
                                                onChange={() => handleSelectContentCreator(creator.influencerId)}
                                                label=""
                                                className="me-3"
                                            />
                                            <span>{creator.creatorName}</span>
                                        </MDBListGroupItem>
                                    ))}
                                </MDBListGroup>
                            </MDBDropdownMenu>
                        </MDBDropdown>
                    </Form.Group>

                    {/* Content URL Input */}
                    <Form.Group className="mb-3">
                        <Form.Label>Content URL *</Form.Label>
                        <Form.Control
                            type="text"
                            value={contentUrl}
                            onChange={(e) => setContentUrl(e.target.value)}
                        />
                    </Form.Group>

                    {/* Workplace Dropdown */}
                    <Form.Group className="mb-3">
                        <Form.Label>Work Platform *</Form.Label>
                        <Form.Select value={workplatform} onChange={(e) => setWorkplatform(e.target.value)}>
                            <option value="">Select Work Platform</option>
                            <option value="9bb8913b-ddd9-430b-a66a-d74d846e6c66">Instagram</option>
                            <option value="de55aeec-0dc8-4119-bf90-16b3d1f0c987">TikTok</option>
                        </Form.Select>
                    </Form.Group>
                    {/* Description Input */}
                    <Form.Group className="mb-3">
                        <Form.Label>Description *</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </Form.Group>


                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>Close</Button>
                <Button variant="info" onClick={handleAddMore}>Add More</Button>
                <Button variant="primary" onClick={handleSave}>Save</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ContentForm;
